import { RowCollection } from './RowCollection';
import { GridColumn } from './GridColumn';
import { GridCell } from './GridCell';
import { enumerable } from '@Common/Core/CoreDecorator';

export class GridRow<TData = any> {
    private _dataIndex: number;
    private _own: RowCollection;
    private _tableRow: JQuery;
    private _cells: GridCell[] = [];
    private initilized: boolean = false;
    private isTableRowBinded: boolean = false;
    private _isPinned: boolean = false;

    constructor(dataIndex: number, tableRow: JQuery, own: RowCollection) {
        this._dataIndex = dataIndex;
        this._tableRow = tableRow;
        this._own = own;
        this._own.grid.columns().forEach((gridColumn: GridColumn) => {
            this._cells[gridColumn.columnIndex()] = new GridCell(this, gridColumn);
        });
        for (let i = 0; i < this._cells.length; i++) {
            this._cells[i].init();
        }
        this.initilized = true;
    }

    @enumerable
    public get isRendered(): boolean {
        return this._own.isRenderedRow(this);
    }

    @enumerable
    public get isPinned(): boolean {
        return this._isPinned;
    }

    public set isPinned(val: boolean) {
        this._isPinned = val;
    }

    public isVisible(value?: boolean): boolean {
        if (typeof value == 'undefined') {
            return this._own.getRowVisibility(this);
        } else {
            this._own.setRowVisibility(this, value);
        }
        return value;
    }

    public tableRow(): JQuery {
        return this._tableRow;
    }

    public dataIndex(): number {
        return this._dataIndex;
    }

    public orderedIndex(): number {
        return this._own.getOrderedIndex(this);
    }

    public getValue(index: GridColumn): any {
        return this._own.getValue(this, index);
    }

    public setValue(index: GridColumn, value: any): void {
        this._own.setValue(this, index, value);
    }

    public cells(): GridCell[] {
        return this._cells;
    }

    public getData(): TData {
        return this._own.getData(this);
    }

    public setData(value: any): void {
        this._own.setData(this, value);
    }

    public isSelected(val?: boolean): boolean {
        if (typeof val === 'undefined') {
            return RowCollection.isSelectedRow(this);
        }

        return this._own.setSelectedRow(this, val);
    }

    public getCell(dataIndex: keyof TData): GridCell;
    public getCell(index: number): GridCell;
    public getCell(gridColumn: GridColumn): GridCell;
    public getCell(column: GridColumn | number | string): GridCell {
        const col = column instanceof GridColumn ? column : this._own.grid.findColumn(column);
        return this._cells[col.columnIndex()];
    }

    public setBindIndex(index: number) {
        this._dataIndex = index;
    }

    public refresh(redraw: boolean = false) {
        if (!this.initilized) {
            return;
        }

        if (this.isRendered && !this.isTableRowBinded) {
            this.isTableRowBinded = true;
            this._tableRow.data('rowInstance', this);
        }

        for (let i = 0; i < this._cells.length; i++) {
            this._cells[i].refresh(redraw);
        }
    }

    public remove(): void {
        this._own.deleteRows(this.dataIndex());
    }

    public findNextEditableCell(column?: GridColumn, leftToRight: boolean = true): GridCell {
        return leftToRight ? this.findLeftToRight(column) : this.findRightToLeft(column);
    }

    private findRightToLeft(column?: GridColumn): GridCell {
        const startIndex = column != null ? column.columnIndex() : this._cells.length;
        for (let i = startIndex - 1; i >= 0; i--) {
            const cell = this._cells[i];
            if (cell.canTab()) {
                return cell;
            }
        }
        return null;
    }

    private findLeftToRight(column?: GridColumn): GridCell {
        const startIndex = column != null ? column.columnIndex() : -1;
        for (let i = startIndex + 1; i < this._cells.length; i++) {
            const cell = this._cells[i];
            if (cell.canTab()) {
                return cell;
            }
        }
        return null;
    }

    public ensureVisible() {
        this._own.ensureVisible(this);
    }
}
